/**
 * Form Details Page
 * 
 * Displays a document template with its:
 * - Extracted fields from the PDF
 * - AI-generated questionnaire
 * - Status and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import { useToast } from '../Context/ToastContext';
import { useConfirm } from '../Context/ConfirmContext';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  background: linear-gradient(135deg, #0a0d12 0%, #111820 50%, #0d1117 100%);
  padding: 40px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(71, 85, 105, 0.3);
  border: 1px solid rgba(71, 85, 105, 0.4);
  border-radius: 8px;
  color: #94a3b8;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(71, 85, 105, 0.5);
    color: #e2e8f0;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #FFFFFF;
  background: linear-gradient(135deg, #fff 0%, #94a3b8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(148, 163, 184, 0.8);
  font-size: 14px;
`;

const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$color || 'rgba(59, 130, 246, 0.2)'};
  color: ${props => props.$textColor || '#60a5fa'};
`;

const StatusBadge = styled(Badge)`
  ${props => props.$status === 'ready' && css`
    background: rgba(16, 185, 129, 0.15);
    color: #34d399;
  `}
  ${props => props.$status === 'generating' && css`
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    animation: ${pulse} 1.5s ease-in-out infinite;
  `}
  ${props => props.$status === 'failed' && css`
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
  `}
  ${props => props.$status === 'pending' && css`
    background: rgba(251, 191, 36, 0.15);
    color: #fbbf24;
  `}
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    border: none;
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
    }
  ` : css`
    background: rgba(71, 85, 105, 0.3);
    border: 1px solid rgba(71, 85, 105, 0.4);
    color: #94a3b8;
    
    &:hover {
      background: rgba(71, 85, 105, 0.5);
      color: #e2e8f0;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(71, 85, 105, 0.3);
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(10px);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(30, 41, 59, 0.6);
  border-bottom: 1px solid rgba(71, 85, 105, 0.2);
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CardContent = styled.div`
  padding: 24px;
  max-height: 500px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(71, 85, 105, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(71, 85, 105, 0.4);
    border-radius: 3px;
  }
`;

const FieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldItem = styled.div`
  padding: 12px 16px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(71, 85, 105, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FieldInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FieldName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FieldMeta = styled.div`
  font-size: 11px;
  color: rgba(148, 163, 184, 0.6);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldType = styled.span`
  padding: 2px 8px;
  background: rgba(59, 130, 246, 0.15);
  border-radius: 4px;
  font-size: 10px;
  color: #60a5fa;
`;

const MappedBadge = styled.span`
  padding: 2px 8px;
  background: ${props => props.$mapped ? 'rgba(16, 185, 129, 0.15)' : 'rgba(251, 191, 36, 0.15)'};
  border-radius: 4px;
  font-size: 10px;
  color: ${props => props.$mapped ? '#34d399' : '#fbbf24'};
`;

// Questionnaire Styles
const SectionBlock = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuestionItem = styled.div`
  padding: 14px 16px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(71, 85, 105, 0.2);
  border-radius: 10px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const QuestionNumber = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  margin-right: 8px;
`;

const QuestionText = styled.div`
  font-size: 13px;
  color: #e2e8f0;
  line-height: 1.5;
`;

const QuestionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const QuestionBadge = styled.span`
  padding: 2px 8px;
  background: ${props => props.$color || 'rgba(71, 85, 105, 0.3)'};
  border-radius: 4px;
  font-size: 10px;
  color: ${props => props.$textColor || '#94a3b8'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 24px;
  color: rgba(148, 163, 184, 0.6);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.4;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 60px auto;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 16px;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 12px;
  margin-bottom: 16px;
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.$color || '#3b82f6'};
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: rgba(148, 163, 184, 0.6);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// ============================================
// COMPONENT
// ============================================

const FormDetails = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const axios = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  
  // Generate Link state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkFormData, setLinkFormData] = useState({ email: '', name: '' });
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  
  // Load template and questionnaire
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/document-templates/${templateId}/full`);
      
      if (response.data.success) {
        setTemplate(response.data.template);
        setQuestionnaire(response.data.questionnaire);
      } else {
        setError('Failed to load template');
      }
    } catch (err) {
      console.error('Error loading template:', err);
      setError(err.response?.data?.error || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [axios, templateId]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Poll for questionnaire if generating
  useEffect(() => {
    if (template?.questionnaireStatus === 'generating') {
      const interval = setInterval(loadData, 5000); // Poll every 5 seconds
      return () => clearInterval(interval);
    }
  }, [template?.questionnaireStatus, loadData]);
  
  // Regenerate questionnaire
  const handleRegenerate = async () => {
    const confirmed = await confirm({
      title: 'Regenerate Questionnaire',
      message: 'This will regenerate the questionnaire. Any customizations will be lost. Continue?',
      confirmText: 'Regenerate',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) return;
    
    setRegenerating(true);
    try {
      const response = await axios.post(`/document-templates/${templateId}/regenerate-questionnaire`);
      
      if (response.data.success) {
        setQuestionnaire(response.data.questionnaire);
        setTemplate(prev => ({ ...prev, questionnaireStatus: 'ready', totalQuestions: response.data.questionnaire?.totalQuestions || 0 }));
      } else {
        setError('Failed to regenerate questionnaire');
      }
    } catch (err) {
      console.error('Error regenerating:', err);
      setError(err.response?.data?.error || 'Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };
  
  // Generate form filling link and send email
  const handleGenerateLink = async () => {
    if (!linkFormData.email) {
      showToast('Please enter user email', 'warning');
      return;
    }
    
    setGeneratingLink(true);
    try {
      // Create session and get password
      const response = await axios.post('/form-filling/sessions', {
        formTemplateId: templateId,
        userEmail: linkFormData.email,
        userName: linkFormData.name || undefined
      });
      
      if (response.data.success) {
        const { sessionUrl, accessPassword, userEmail, userName } = response.data;
        setGeneratedLink(sessionUrl);
        
        // Send email with link and password
        try {
          await fetch('https://mailstrom-cz3mc.ondigitalocean.app/meetings/form-filling', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: userEmail,
              userName: userName || linkFormData.name || 'Valued Client',
              expertName: 'Your Advisor', // TODO: Get from user context
              formName: template?.name || 'Application Form',
              formLink: sessionUrl,
              accessPassword: accessPassword,
              expiresAt: new Date(response.data.session.expiresAt).toLocaleDateString()
            })
          });
          showToast('📧 Email sent to user with link and password!', 'success');
        } catch (emailErr) {
          console.error('Failed to send email:', emailErr);
          showToast('Link generated but email failed to send. Please share manually.', 'warning');
        }
      } else {
        setError(response.data.error || 'Failed to generate link');
      }
    } catch (err) {
      console.error('Error generating link:', err);
      setError(err.response?.data?.error || 'Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };
  
  // Copy link to clipboard
  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      showToast('Link copied to clipboard!', 'success');
    }
  };
  
  // Reset link modal
  const closeLinkModal = () => {
    setShowLinkModal(false);
    setLinkFormData({ email: '', name: '' });
    setGeneratedLink(null);
  };
  
  if (loading) {
    return (
      <PageWrapper>
        <LoadingSpinner />
      </PageWrapper>
    );
  }
  
  if (error || !template) {
    return (
      <PageWrapper>
        <Container>
          <BackButton onClick={() => navigate(-1)}>
            ← Back
          </BackButton>
          <EmptyState>
            <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
            <div style={{ fontSize: 16, color: '#f87171' }}>{error || 'Template not found'}</div>
          </EmptyState>
        </Container>
      </PageWrapper>
    );
  }
  
  // Count questions
  let totalQuestions = 0;
  let requiredQuestions = 0;
  if (questionnaire?.sections) {
    questionnaire.sections.forEach(section => {
      totalQuestions += section.questions?.length || 0;
      requiredQuestions += section.questions?.filter(q => q.required).length || 0;
    });
  }
  
  return (
    <PageWrapper>
      <Container>
        {/* Header */}
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate(-1)}>
              ← Back to Templates
            </BackButton>
            <Title>{template.name}</Title>
            <Subtitle>
              <Badge>{template.category}</Badge>
              <span>{template.formCode}</span>
              <span>•</span>
              <StatusBadge $status={template.questionnaireStatus}>
                {template.questionnaireStatus === 'ready' && '✓ Questionnaire Ready'}
                {template.questionnaireStatus === 'generating' && '⏳ Generating...'}
                {template.questionnaireStatus === 'failed' && '❌ Generation Failed'}
                {template.questionnaireStatus === 'pending' && '⏳ Pending'}
                {(!template.questionnaireStatus || template.questionnaireStatus === 'none') && 'No Questionnaire'}
              </StatusBadge>
            </Subtitle>
          </HeaderLeft>
          
          <HeaderActions>
            <ActionButton 
              onClick={handleRegenerate}
              disabled={regenerating || template.questionnaireStatus === 'generating'}
            >
              {regenerating ? '⏳ Regenerating...' : '🔄 Regenerate Questionnaire'}
            </ActionButton>
            <ActionButton $primary onClick={() => setShowLinkModal(true)}>
              📋 Generate Link
            </ActionButton>
          </HeaderActions>
        </Header>
        
        {/* Stats */}
        <StatRow>
          <Stat>
            <StatValue $color="#3b82f6">{template.totalFields || 0}</StatValue>
            <StatLabel>Form Fields</StatLabel>
          </Stat>
          <Stat>
            <StatValue $color="#10b981">{template.mappedFields || 0}</StatValue>
            <StatLabel>Auto-Mapped</StatLabel>
          </Stat>
          <Stat>
            <StatValue $color="#8b5cf6">{totalQuestions}</StatValue>
            <StatLabel>Questions</StatLabel>
          </Stat>
          <Stat>
            <StatValue $color="#f59e0b">{requiredQuestions}</StatValue>
            <StatLabel>Required</StatLabel>
          </Stat>
          <Stat>
            <StatValue $color="#06b6d4">{questionnaire?.estimatedMinutes || '—'}</StatValue>
            <StatLabel>Est. Minutes</StatLabel>
          </Stat>
        </StatRow>
        
        {/* Grid */}
        <Grid>
          {/* Questionnaire */}
          <Card>
            <CardHeader>
              <CardTitle>
                <span>📋</span>
                AI-Generated Questionnaire
              </CardTitle>
              {questionnaire && (
                <Badge $color="rgba(139, 92, 246, 0.15)" $textColor="#a78bfa">
                  {totalQuestions} questions
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {template.questionnaireStatus === 'generating' ? (
                <EmptyState>
                  <LoadingSpinner style={{ margin: '20px auto' }} />
                  <div>Generating questionnaire with AI...</div>
                  <div style={{ fontSize: 12, marginTop: 8 }}>This may take a minute</div>
                </EmptyState>
              ) : questionnaire?.sections?.length > 0 ? (
                questionnaire.sections.map((section, sIdx) => (
                  <SectionBlock key={section.id || sIdx}>
                    <SectionTitle>
                      <span>📁</span>
                      {section.title}
                    </SectionTitle>
                    {section.questions?.map((question, qIdx) => (
                      <QuestionItem key={question.id || qIdx}>
                        <QuestionText>
                          <QuestionNumber>Q{qIdx + 1}.</QuestionNumber>
                          {question.question}
                        </QuestionText>
                        <QuestionMeta>
                          <QuestionBadge $color="rgba(59, 130, 246, 0.15)" $textColor="#60a5fa">
                            {question.type}
                          </QuestionBadge>
                          {question.required && (
                            <QuestionBadge $color="rgba(239, 68, 68, 0.15)" $textColor="#f87171">
                              Required
                            </QuestionBadge>
                          )}
                          {question.linkedFields?.length > 0 && (
                            <QuestionBadge>
                              → {question.linkedFields.length} field{question.linkedFields.length > 1 ? 's' : ''}
                            </QuestionBadge>
                          )}
                        </QuestionMeta>
                      </QuestionItem>
                    ))}
                  </SectionBlock>
                ))
              ) : (
                <EmptyState>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                  <div>No questionnaire generated yet</div>
                  <div style={{ fontSize: 12, marginTop: 8 }}>
                    Click "Regenerate Questionnaire" to create one
                  </div>
                </EmptyState>
              )}
            </CardContent>
          </Card>
          
          {/* Form Fields */}
          <Card>
            <CardHeader>
              <CardTitle>
                <span>📄</span>
                PDF Form Fields
              </CardTitle>
              <Badge>
                {template.mappedFields}/{template.totalFields} mapped
              </Badge>
            </CardHeader>
            <CardContent>
              {template.fields?.length > 0 ? (
                <FieldsList>
                  {template.fields.map((field, idx) => (
                    <FieldItem key={field.fieldName || idx}>
                      <FieldInfo>
                        <FieldName>{field.displayName || field.fieldName}</FieldName>
                        <FieldMeta>
                          <FieldType>{field.fieldType}</FieldType>
                          <MappedBadge $mapped={!!field.dataPath}>
                            {field.dataPath ? `→ ${field.dataPath}` : 'Unmapped'}
                          </MappedBadge>
                        </FieldMeta>
                      </FieldInfo>
                    </FieldItem>
                  ))}
                </FieldsList>
              ) : (
                <EmptyState>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
                  <div>No fields found in this PDF</div>
                </EmptyState>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Generate Link Modal */}
        {showLinkModal && (
          <ModalOverlay onClick={closeLinkModal}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalTitle>Generate Form Filling Link</ModalTitle>
              
              {!generatedLink ? (
                <>
                  <ModalText>
                    Create a unique link for a user to fill out this form. 
                    The AI will guide them through all the questions.
                  </ModalText>
                  
                  <FormGroup>
                    <Label>User Email *</Label>
                    <Input
                      type="email"
                      value={linkFormData.email}
                      onChange={e => setLinkFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="user@example.com"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>User Name (optional)</Label>
                    <Input
                      value={linkFormData.name}
                      onChange={e => setLinkFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Smith"
                    />
                  </FormGroup>
                  
                  <ModalActions>
                    <ModalButton onClick={closeLinkModal}>Cancel</ModalButton>
                    <ModalButton $primary onClick={handleGenerateLink} disabled={generatingLink}>
                      {generatingLink ? 'Generating...' : 'Generate Link'}
                    </ModalButton>
                  </ModalActions>
                </>
              ) : (
                <>
                  <SuccessIcon>✓</SuccessIcon>
                  <ModalText style={{ color: '#34d399' }}>Email Sent Successfully!</ModalText>
                  
                  <div style={{ 
                    background: 'rgba(16, 185, 129, 0.1)', 
                    border: '1px solid rgba(16, 185, 129, 0.3)', 
                    borderRadius: '12px', 
                    padding: '16px', 
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', color: '#34d399', marginBottom: '8px' }}>
                      📧 Email sent to <strong>{linkFormData.email}</strong>
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      The user will receive a link and password to access the form
                    </div>
                  </div>
                  
                  <ModalText style={{ fontSize: 12, marginTop: 12, color: '#64748b' }}>
                    <strong>Backup Link:</strong> In case the email doesn't arrive, you can share this link manually:
                  </ModalText>
                  
                  <LinkBox>
                    <LinkText>{generatedLink}</LinkText>
                    <CopyButton onClick={copyToClipboard}>📋 Copy</CopyButton>
                  </LinkBox>
                  
                  <ModalActions>
                    <ModalButton $primary onClick={closeLinkModal}>Done</ModalButton>
                  </ModalActions>
                </>
              )}
            </Modal>
          </ModalOverlay>
        )}
      </Container>
    </PageWrapper>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(71, 85, 105, 0.4);
  border-radius: 16px;
  padding: 28px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0 0 16px 0;
`;

const ModalText = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.8);
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(71, 85, 105, 0.4);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  
  &:focus {
    border-color: #3b82f6;
  }
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.4);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    border: none;
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    }
  ` : `
    background: rgba(71, 85, 105, 0.3);
    border: 1px solid rgba(71, 85, 105, 0.4);
    color: #94a3b8;
    
    &:hover {
      background: rgba(71, 85, 105, 0.5);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const LinkBox = styled.div`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(71, 85, 105, 0.4);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LinkText = styled.div`
  flex: 1;
  font-size: 13px;
  color: #60a5fa;
  word-break: break-all;
`;

const CopyButton = styled.button`
  padding: 8px 12px;
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 6px;
  color: #60a5fa;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    background: rgba(59, 130, 246, 0.3);
  }
`;

export default FormDetails;

